/** Unwrap .NET-style { statusCode, data } responses from the Python API. */
export function unwrapApiPayload(payload) {
  if (payload == null) return null;
  if (typeof payload !== 'object' || Array.isArray(payload)) return payload;

  const hasEnvelope =
    payload.statusCode != null || payload.status != null || payload.message != null;

  if (hasEnvelope && ('data' in payload || 'Data' in payload)) {
    return payload.data ?? payload.Data ?? null;
  }
  return payload;
}

export function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value?.$values && Array.isArray(value.$values)) return value.$values;
  return [];
}

export function normalizeAssessmentCycles(rawList) {
  return asArray(rawList)
    .map((cycle) => ({
      id: cycle?.id ?? cycle?.Id,
      assessmentCycleName:
        cycle?.assessmentCycleName ?? cycle?.AssessmentCycleName ?? cycle?.name ?? '',
    }))
    .filter((cycle) => cycle.id != null && cycle.assessmentCycleName);
}

/** Normalize a single assessment row from PascalCase or camelCase API payloads. */
export function normalizeAssessmentRow(row) {
  if (!row) return row;
  return {
    ...row,
    id: row.id ?? row.Id,
    title: row.title ?? row.Title,
    skillName: row.skillName ?? row.SkillName,
    skill_Id: row.skill_Id ?? row.Skill_Id,
    responseStatus: row.responseStatus ?? row.ResponseStatus,
    userScore: row.userScore ?? row.UserScore ?? 0,
    assessmentType: row.assessmentType ?? row.AssessmentType,
    responseID: row.responseID ?? row.ResponseID,
  };
}

export function normalizeAssessmentLists(data) {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    primaryAssessmentList: asArray(
      data.primaryAssessmentList ?? data.PrimaryAssessmentList
    ).map(normalizeAssessmentRow),
    secondaryAssessmentList: asArray(
      data.secondaryAssessmentList ?? data.SecondaryAssessmentList
    ).map(normalizeAssessmentRow),
  };
}

/** Normalize QbrAdminList / SME status row for Approve Review Modifications grid. */
export function normalizeSmeStatusRow(item, index = 0) {
  if (!item || typeof item !== 'object') return null;
  const samId = item.samId ?? item.sAMId ?? item.SAMId ?? '';
  const cycleId = item.cycleId ?? item.cycleID ?? item.CycleID ?? item.CycleId ?? '';
  const cycleName = item.cycleName ?? item.CycleName ?? '';
  const userName = item.userName ?? item.UserName ?? '';
  const rowKey = samId || userName || `sme-${index}`;
  return {
    id: `${rowKey}-${cycleId || cycleName || index}`,
    cycleName,
    cycleId,
    samId,
    skillName: item.skillName ?? item.SkillName ?? '',
    userName,
    emailId: item.emailId ?? item.EmailId ?? '',
    totalQuestions: Number(item.totalQuestions ?? item.TotalQuestions ?? 0),
    pendingQuestions: Number(item.pendingQuestions ?? item.PendingQuestions ?? 0),
    reviewedQuestion: Number(item.reviewedQuestion ?? item.ReviewedQuestion ?? 0),
    markedForDelete: Number(item.markedForDelete ?? item.MarkedForDelete ?? 0),
    adminReviwed: Number(item.adminReviwed ?? item.AdminReviwed ?? 0),
    adminReviwePending: Number(item.adminReviwePending ?? item.AdminReviwePending ?? 0),
  };
}

export function normalizeSmeStatusList(payload) {
  const envelope = payload ?? {};
  const inner = unwrapApiPayload(envelope) ?? envelope?.data ?? envelope?.Data ?? envelope;
  if (Array.isArray(inner)) {
    return asArray(inner).map(normalizeSmeStatusRow).filter(Boolean);
  }
  const nested = inner?.data ?? inner?.Data;
  const container = nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : inner;
  const rawList =
    container?.listSMEStatusModels ??
    container?.ListSMEStatusModels ??
    container?.listSmeStatusModels ??
    [];
  return asArray(rawList).map(normalizeSmeStatusRow).filter(Boolean);
}

export function normalizeQuestionOption(opt) {
  if (!opt) return opt;
  return {
    ...opt,
    optionId: opt.optionId ?? opt.OptionId,
    optionPrompt: opt.optionPrompt ?? opt.OptionPrompt,
    isCorrectOption: opt.isCorrectOption ?? opt.IsCorrectOption,
  };
}

export function parseQuestionMeta(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function normalizeQuestionRow(q) {
  if (!q) return q;
  const questionMeta = q.questionMeta ?? q.QuestionMeta;
  const parsedMeta = parseQuestionMeta(questionMeta);
  return {
    ...q,
    questionId: q.questionId ?? q.QuestionId,
    questionPrompt: q.questionPrompt ?? q.QuestionPrompt,
    questionType: q.questionType ?? q.QuestionType,
    complexity: q.complexity ?? q.Complexity,
    questionMeta: parsedMeta ?? questionMeta,
    correctOrder:
      q.correctOrder ??
      q.CorrectOrder ??
      parsedMeta?.correctOrder ??
      parsedMeta?.correct_order,
    createdOn: q.createdOn ?? q.CreatedOn,
    createdBy: q.createdBy ?? q.CreatedBy,
    originalId: q.originalId ?? q.OriginalId,
    previousId: q.previousId ?? q.PreviousId,
    attachmentDataURL: q.attachmentDataURL ?? q.AttachmentDataURL,
    options: asArray(q.options ?? q.Options).map(normalizeQuestionOption),
  };
}

/** Normalize FillAssessment/TakeAssessment API payload to camelCase. */
export function normalizeTakeAssessmentPayload(data) {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    assessmentTitle: data.assessmentTitle ?? data.AssessmentTitle,
    assessmentId: data.assessmentId ?? data.AssessmentId,
    duration: data.duration ?? data.Duration,
    criteria: data.criteria ?? data.Criteria,
    maxQuestions: data.maxQuestions ?? data.MaxQuestions,
    attendedQuestions: data.attendedQuestions ?? data.AttendedQuestions,
    currentCycleId: data.currentCycleId ?? data.CurrentCycleId,
    currentAssessmentId: data.currentAssessmentId ?? data.CurrentAssessmentId,
    currentResponseId: data.currentResponseId ?? data.CurrentResponseId,
    questionTimeRatio: data.questionTimeRatio ?? data.QuestionTimeRatio,
    skillId: data.skillId ?? data.SkillId,
    questions: asArray(data.questions ?? data.Questions).map(normalizeQuestionRow),
  };
}

const CYCLE_STATUS_CODE_TO_ENUM = {
  0: 'Available',
  1: 'InProgress',
  2: 'SubmittedForReview',
  3: 'UnderReview',
  4: 'Completed',
};

/** Map legacy/Python status labels to .NET AssessmentCycleResponseStatus ToString() values. */
export function normalizeCycleResponseStatusValue(statusValue, statusCode) {
  const code = Number(statusCode);
  const raw = statusValue == null ? '' : String(statusValue).trim();
  const lower = raw.toLowerCase();

  if (Number.isFinite(code) && CYCLE_STATUS_CODE_TO_ENUM[code]) {
    if (!raw || lower === 'submitted') {
      return CYCLE_STATUS_CODE_TO_ENUM[code];
    }
  }

  if (lower === 'submitted' || lower === 'submitted for review') return 'SubmittedForReview';
  if (lower === 'in progress' || lower === 'inprogress') return 'InProgress';
  if (lower === 'under review' || lower === 'underreview') return 'UnderReview';
  if (lower === 'completed') return 'Completed';
  if (lower === 'available') return 'Available';

  if (Number.isFinite(code) && CYCLE_STATUS_CODE_TO_ENUM[code]) {
    return CYCLE_STATUS_CODE_TO_ENUM[code];
  }

  return raw;
}

/** Normalize assessor Employee List row (.NET, camelCase, or alternate Python API shapes). */
export function normalizeAssessorEmployeeListUser(user) {
  if (!user || typeof user !== 'object') return user;

  const statusCode = Number(
    user.CurrentCycleStatus ??
      user.currentCycleStatus ??
      user.Status ??
      user.status ??
      0
  );

  const statusValue = normalizeCycleResponseStatusValue(
    user.CurrentCycleStatusValue ??
      user.currentCycleStatusValue ??
      user.StatusValue ??
      user.statusValue,
    statusCode
  );

  return {
    ...user,
    SamId: user.SamId ?? user.samId,
    UserName: user.UserName ?? user.userName,
    EmailId: user.EmailId ?? user.emailId,
    ManagerName: user.ManagerName ?? user.managerName,
    Skill: user.Skill ?? user.skill,
    Proficiency: user.Proficiency ?? user.proficiency ?? '',
    CurrentCycleId:
      user.CurrentCycleId ?? user.currentCycleId ?? user.CycleId ?? user.cycleId ?? 0,
    CurrentCycleStatus: Number.isFinite(statusCode) ? statusCode : 0,
    CurrentCycleStatusValue: statusValue,
    CurrentCycleScore:
      user.CurrentCycleScore ?? user.currentCycleScore ?? user.Score ?? user.score ?? 0,
    ResponseId: user.ResponseId ?? user.responseId ?? 0,
  };
}

export function normalizeAssessorEmployeeListUsers(users) {
  return asArray(users).map(normalizeAssessorEmployeeListUser);
}

export function isAssessorCurrentCycleEnabled(row) {
  const status = normalizeAssessorEmployeeListUser(row).CurrentCycleStatusValue;
  return (
    status === 'SubmittedForReview' || status === 'UnderReview' || status === 'Completed'
  );
}

export function normalizeAssessorFeedbackRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.id ?? row.Id ?? 0,
    assessmentCycleResponse_Id: row.assessmentCycleResponse_Id ?? row.AssessmentCycleResponse_Id ?? 0,
    assessmentCategory_Id: Number(row.assessmentCategory_Id ?? row.AssessmentCategory_Id ?? 0),
    assessorComments: row.assessorComments ?? row.AssessorComments ?? '',
    assessorWeightage: Number(row.assessorWeightage ?? row.AssessorWeightage ?? 0),
  };
}

export function normalizeAssessorFeedbackList(raw) {
  return asArray(raw).map(normalizeAssessorFeedbackRow);
}

export function sumAssessorFeedbackWeightages(feedbackList) {
  return asArray(feedbackList).reduce(
    (sum, row) => sum + Number(row?.assessorWeightage ?? 0),
    0
  );
}
